import { type ReactNode, useState } from 'react';
import type { RetornoLinha } from '@pcp/contracts';
import { isConflict, isOk } from '../api/cadastros';
import { t } from '../i18n/pt-BR';

type Primitive = string | number | boolean;

export type Editor<T> =
  | { kind: 'number'; field: keyof T & string; step?: number; min?: number; max?: number }
  | { kind: 'text'; field: keyof T & string; width?: number }
  | { kind: 'check'; field: keyof T & string }
  | {
      kind: 'select';
      field: keyof T & string;
      options: { value: string | number; label: string }[];
      /** converte o valor escolhido de volta para número */
      numeric?: boolean;
    };

export interface Column<T> {
  key: string;
  header: string;
  numeric?: boolean;
  /** como a célula aparece quando a linha não está em edição (ou a coluna é só leitura) */
  read: (row: T) => ReactNode;
  /** ausente = coluna só leitura (chave do registro, valor calculado pelo ABL…) */
  edit?: Editor<T>;
}

type RowStatus =
  | { kind: 'saving' }
  | { kind: 'saved' }
  | { kind: 'conflict'; message: string }
  | { kind: 'error'; message: string };

interface Props<T> {
  rows: T[];
  rowKey: (row: T) => string;
  rowLabel: (row: T) => string;
  columns: Column<T>[];
  canEdit: boolean;
  /** grava só as alterações da linha; devolve a linha de retorno do lote */
  onSave: (row: T, changes: Partial<T>) => Promise<RetornoLinha>;
  onDelete?: (row: T) => Promise<RetornoLinha>;
  /** recarrega os dados do servidor (descarta o rascunho da linha em conflito) */
  onReload: () => void;
  emptyText?: string;
}

/**
 * Tabela editável em linha, com "salvar por linha". Cada linha carrega o `version` lido junto com ela;
 * se o ABL devolver conflito (409), a linha mostra o aviso e oferece recarregar — nunca sobrescreve.
 */
export function EditableTable<T>({
  rows,
  rowKey,
  rowLabel,
  columns,
  canEdit,
  onSave,
  onDelete,
  onReload,
  emptyText,
}: Props<T>) {
  const [drafts, setDrafts] = useState<Record<string, Partial<T>>>({});
  const [status, setStatus] = useState<Record<string, RowStatus>>({});
  const [confirming, setConfirming] = useState<string | null>(null);

  const setField = (key: string, field: string, value: Primitive | undefined) => {
    setDrafts((d) => ({ ...d, [key]: { ...d[key], [field]: value } as Partial<T> }));
    setStatus((s) => {
      if (!s[key]) return s;
      const { [key]: _removed, ...rest } = s;
      return rest;
    });
  };
  const clear = (key: string) => {
    setDrafts((d) => {
      const { [key]: _x, ...rest } = d;
      return rest;
    });
    setStatus((s) => {
      const { [key]: _x, ...rest } = s;
      return rest;
    });
  };
  const valueOf = (row: T, draft: Partial<T> | undefined, field: string) =>
    (draft && field in draft
      ? (draft as Record<string, unknown>)[field]
      : (row as Record<string, unknown>)[field]) as Primitive | undefined;
  const invalid = (draft: Partial<T>) =>
    Object.values(draft as Record<string, unknown>).some((v) => typeof v === 'number' && Number.isNaN(v));

  const save = async (row: T) => {
    const key = rowKey(row);
    const changes = drafts[key];
    if (!changes) return;
    setStatus((s) => ({ ...s, [key]: { kind: 'saving' } }));
    try {
      const r = await onSave(row, changes);
      if (isOk(r)) {
        clear(key);
        setStatus((s) => ({ ...s, [key]: { kind: 'saved' } }));
      } else if (isConflict(r)) {
        setStatus((s) => ({ ...s, [key]: { kind: 'conflict', message: r.errordescription } }));
      } else {
        setStatus((s) => ({ ...s, [key]: { kind: 'error', message: r.errordescription } }));
      }
    } catch (e) {
      setStatus((s) => ({
        ...s,
        [key]: { kind: 'error', message: e instanceof Error ? e.message : t.states.datasulDown },
      }));
    }
  };

  const remove = async (row: T) => {
    if (!onDelete) return;
    const key = rowKey(row);
    setConfirming(null);
    setStatus((s) => ({ ...s, [key]: { kind: 'saving' } }));
    try {
      const r = await onDelete(row);
      if (isOk(r)) clear(key);
      else if (isConflict(r))
        setStatus((s) => ({ ...s, [key]: { kind: 'conflict', message: r.errordescription } }));
      else setStatus((s) => ({ ...s, [key]: { kind: 'error', message: r.errordescription } }));
    } catch (e) {
      setStatus((s) => ({
        ...s,
        [key]: { kind: 'error', message: e instanceof Error ? e.message : t.states.datasulDown },
      }));
    }
  };

  if (!rows.length) return <p className="hint">{emptyText ?? t.cadastros.empty}</p>;

  return (
    <div className="scroll">
      <table className="cad-table">
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.numeric ? 'n' : undefined}>
                {c.header}
              </th>
            ))}
            {canEdit ? <th className="actions-col">{t.cadastros.actions}</th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const key = rowKey(row);
            const draft = drafts[key];
            const dirty = !!draft && Object.keys(draft).length > 0;
            const st = status[key];
            return (
              <tr key={key} className={dirty ? 'dirty' : st?.kind === 'conflict' ? 'conflict' : undefined}>
                {columns.map((c) => {
                  const ed = canEdit ? c.edit : undefined;
                  if (!ed) {
                    return (
                      <td key={c.key} className={c.numeric ? 'n' : undefined}>
                        {c.read(row)}
                      </td>
                    );
                  }
                  const label = `${c.header} — ${rowLabel(row)}`;
                  const value = valueOf(row, draft, ed.field);
                  return (
                    <td key={c.key} className={c.numeric ? 'n' : undefined}>
                      {ed.kind === 'check' ? (
                        <input
                          type="checkbox"
                          aria-label={label}
                          checked={!!value}
                          onChange={(e) => setField(key, ed.field, e.target.checked)}
                        />
                      ) : ed.kind === 'number' ? (
                        <input
                          type="number"
                          className="cell-input n"
                          aria-label={label}
                          value={typeof value === 'number' && !Number.isNaN(value) ? value : ''}
                          step={ed.step ?? 'any'}
                          min={ed.min}
                          max={ed.max}
                          onChange={(e) =>
                            setField(key, ed.field, e.target.value === '' ? NaN : Number(e.target.value))
                          }
                        />
                      ) : ed.kind === 'select' ? (
                        <select
                          className="cell-input"
                          aria-label={label}
                          value={String(value ?? '')}
                          onChange={(e) =>
                            setField(key, ed.field, ed.numeric ? Number(e.target.value) : e.target.value)
                          }
                        >
                          {ed.options.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          className="cell-input"
                          aria-label={label}
                          value={String(value ?? '')}
                          style={ed.width ? { width: ed.width } : undefined}
                          onChange={(e) => setField(key, ed.field, e.target.value)}
                        />
                      )}
                    </td>
                  );
                })}
                {canEdit ? (
                  <td className="actions-col">
                    <div className="row-actions">
                      <button
                        type="button"
                        className="btn"
                        disabled={!dirty || invalid(draft ?? {}) || st?.kind === 'saving'}
                        onClick={() => void save(row)}
                        aria-label={`${t.cadastros.save} — ${rowLabel(row)}`}
                      >
                        {t.cadastros.save}
                      </button>
                      {dirty ? (
                        <button type="button" className="btn ghost" onClick={() => clear(key)}>
                          {t.cadastros.undo}
                        </button>
                      ) : null}
                      {onDelete && !dirty ? (
                        confirming === key ? (
                          <>
                            <button type="button" className="btn" onClick={() => void remove(row)}>
                              {t.cadastros.confirmDelete}
                            </button>
                            <button type="button" className="btn ghost" onClick={() => setConfirming(null)}>
                              {t.cadastros.cancel}
                            </button>
                          </>
                        ) : (
                          <button
                            type="button"
                            className="btn ghost"
                            aria-label={`${t.cadastros.delete} — ${rowLabel(row)}`}
                            onClick={() => setConfirming(key)}
                          >
                            {t.cadastros.delete}
                          </button>
                        )
                      ) : null}
                    </div>
                    {st?.kind === 'saved' ? (
                      <span className="chip ok" role="status">
                        {t.cadastros.saved}
                      </span>
                    ) : null}
                    {st?.kind === 'conflict' ? (
                      <div className="row-conflict" role="alert">
                        <strong>{t.cadastros.conflictTitle}</strong>
                        <span>{t.cadastros.conflictText}</span>
                        <button
                          type="button"
                          className="btn ghost"
                          onClick={() => {
                            clear(key);
                            onReload();
                          }}
                        >
                          {t.cadastros.reload}
                        </button>
                      </div>
                    ) : null}
                    {st?.kind === 'error' ? (
                      <div className="form-error" role="alert">
                        {st.message}
                      </div>
                    ) : null}
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
